// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { color, getSystemColor } from "@stoneage/XDL/Utilities";
import { css } from "styled-components";

interface ShadowProps {
    shadowWidth?: string;
    shadowColor?: string;
    prevDisabled?: boolean;
    nextDisabled?: boolean;
    persistShadow?: boolean;
}

interface ContainerProps extends ShadowProps {
    direction?: "horizontal" | "vertical";
    height?: number;
    slideMaxWidth?: string;
    isAuto?: boolean;
    arrowsBelowSlides?: boolean;
    withShadow?: boolean;
}

export const xSwiperStyle = css`
    width: 100%;

    .swiper-slide {
        height: auto;
        /* Prevent 1px render gap that Swiper occasionally introduces */
        margin-top: 1px;
        margin-bottom: 1px;
        position: relative;

        > div {
            max-width: 100%;
        }
    }

    /* Hide native Swiper arrows — we render our own */
    .swiper-button-prev,
    .swiper-button-next {
        display: none;
    }

    /* Reset Swiper's opinionated pagination positioning */
    .swiper-pagination-bullets.swiper-pagination-horizontal {
        bottom: initial;
        top: initial;
        left: initial;
        width: initial;
    }

    &&& .swiper-lazy-preloader {
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: ${color("common.black", 0.33)};
        border: 0;
        animation: unset;
        border-radius: 2px;
        margin: 0;
        display: none; /* Lazy-load without showing the spinner for now */

        &::after {
            content: "";
            position: absolute;
            left: 50%;
            top: 50%;
            display: block;
            transform: translate(-50%, -50%);
            animation: swiper-preloader-spin 1s infinite linear;
            width: 40px;
            height: 40px;
            z-index: 10;
            border-radius: 50%;
            border: 4px solid var(--swiper-preloader-color, var(--swiper-theme-color));
            border-top-color: transparent;
        }
    }
`;

// ---------------------------------------------------------------------------
// Edge shadow (fades content toward prev/next boundaries)
// ---------------------------------------------------------------------------
const shadowElHorizontal = css<ShadowProps>`
    content: "";
    position: absolute;
    display: block;
    height: 100%;
    width: ${({ shadowWidth }) => shadowWidth};
    background: ${({ shadowColor }) =>
        `linear-gradient(90deg, ${getSystemColor(shadowColor!, 1)} 0%, ${getSystemColor(shadowColor!, 0)} 100%)`};
    top: 0;
    z-index: 420;
    pointer-events: none;
`;

const shadowElVertical = css<ShadowProps>`
    content: "";
    position: absolute;
    display: block;
    width: 100%;
    height: 30px;
    background: ${({ shadowColor }) =>
        `linear-gradient(180deg, ${getSystemColor(shadowColor!, 1)} 0%, ${getSystemColor(shadowColor!, 0)} 100%)`};
    top: 30px;
    z-index: 420;
    pointer-events: none;
`;

const hideShadow = ({ prevDisabled, nextDisabled, persistShadow }: ShadowProps, side: "prev" | "next") => {
    const isDisabled = side === "prev" ? prevDisabled : nextDisabled;
    return isDisabled && !persistShadow
        ? css`
              display: none;
          `
        : "";
};

export const xSwiperShadow = css<ContainerProps>`
    ${({ withShadow }) =>
        withShadow &&
        css<ShadowProps>`
            .swiper-horizontal::after {
                ${shadowElHorizontal}
                right: -1px;
                transform: scaleX(-1);
                ${props => hideShadow(props, "next")}
            }

            .swiper-horizontal::before {
                ${shadowElHorizontal}
                left: -1px;
                ${props => hideShadow(props, "prev")}
            }

            .swiper-vertical::after {
                ${shadowElVertical}
                top: unset;
                bottom: 15px;
                transform: scaleY(-1);
                ${props => hideShadow(props, "next")}
            }

            .swiper-vertical::before {
                ${shadowElVertical}
                ${props => hideShadow(props, "prev")}
            }
        `}
`;
